import PropTypes from 'prop-types';
import React from 'react';
import { connect } from 'react-redux';

import * as applicationActions from '~actions/application-actions';
import * as uiActions from '~actions/ui-actions';

import K8sApplicationDetailsTable from '~components/applications/k8s-application-details-table';
import LegacyApplicationDetailsTable from '~components/applications/legacy-application-details-table';
import * as dialogTypes from '~components/dialogs/dialog-types';
import ActionButton from '~components/general/action-button';
import GettingMore from '~components/general/getting-more';
import IString from '~components/general/i-string';

import * as applicationScheduler from '~constants/application-scheduler';
import * as applicationStatus from '~constants/application-status';

import { translate } from '~i18n/localize';

import * as applicationSelectors from '~selectors/application-selectors';
import * as taskSelectors from '~selectors/task-selectors';

export function ApplicationDetails({ application, dispatch }) {

  const handleExport = () =>
    dispatch(uiActions.openDialog({
      type: dialogTypes.EXPORT_APPLICATION_DIALOG,
      data: {
        application
      }
    }));

  const handleApplicationTask = (type, confirmAction) => {
    dispatch(uiActions.openDialog({
      type: dialogTypes.APPLICATION_TASK_DIALOG,
      data: {
        application,
        purpose: translate(`application.task.dialog.${type}.purpose`),
        details: translate(`application.task.dialog.${type}.details`),
        onSubmit: () => {
          dispatch(confirmAction);
        },
        onFinish: (task) => {
          if (!taskSelectors.isFailed(task)) {
            dispatch(uiActions.closeDialog());
          }
        }
      }
    }));
  };

  const handleClickDeploy = () => {
    handleApplicationTask('deploy', applicationActions.deployApplication(application.id));
  };

  const handleClickSuspend = () => {
    handleApplicationTask('suspend', applicationActions.suspendApplication(application.id));
  };

  const handleClickResume = () => {
    handleApplicationTask('resume', applicationActions.resumeApplication(application.id));
  };

  const handleClickRestart = () => {
    handleApplicationTask('restart', applicationActions.restartApplication(application.id));
  };

  const handleClickPurge = () => {
    handleApplicationTask('purge', applicationActions.purgeApplication(application.id));
  };

  const handleClickTerminate = () => {
    handleApplicationTask('terminate', applicationActions.terminateApplication(application.id));
  };

  return (
    application
      ? <div className="splitColumns withMobileMargins">
        <div className="column100">

          <div className="sectionTitle flex">
            <IString stringKey="application.details.title" />
            <div className="actionBar inline showTitles">
              { application.exportable && applicationScheduler.isK8s(application) &&
                <ActionButton
                  className="download"
                  onClick={handleExport}
                  title={<IString stringKey="application.details.actions.export" />}
                />
              }
              { applicationStatus.deployable(application) &&
                <ActionButton
                  className="restart"
                  onClick={handleClickDeploy}
                  title={<IString stringKey="application.details.actions.deploy" />}
                />
              }
              { applicationStatus.suspendible(application) &&
                <ActionButton
                  className="reset"
                  onClick={handleClickSuspend}
                  title={<IString stringKey="application.details.actions.suspend" />}
                />
              }
              { applicationStatus.resumable(application) &&
                <ActionButton
                  className="reset"
                  onClick={handleClickResume}
                  title={<IString stringKey="application.details.actions.resume" />}
                />
              }
              { applicationStatus.restartable(application) &&
                <ActionButton
                  className="restart"
                  onClick={handleClickRestart}
                  title={<IString stringKey="application.details.actions.restart" />}
                />
              }
              { applicationStatus.terminateable(application) &&
                <ActionButton
                  className="delete"
                  onClick={handleClickTerminate}
                  title={<IString stringKey="application.details.actions.terminate" />}
                />
              }
              { applicationStatus.purgeable(application) &&
                <ActionButton
                  className="emptyTrash"
                  onClick={handleClickPurge}
                  title={<IString stringKey="application.details.actions.purge" />}
                />
              }
            </div>
          </div>

          { applicationScheduler.isK8s(application)
            ? <K8sApplicationDetailsTable application={application} />
            : <LegacyApplicationDetailsTable application={application} /> }
        </div>
      </div>
      : <GettingMore label={translate('general.loading')} />
  );
}

ApplicationDetails.propTypes = {
  application: PropTypes.shape({
    exportable: PropTypes.bool,
    id: PropTypes.number.isRequired
  }),
  dispatch: PropTypes.func
};

const mapStateToProps = (state) => ({
  application: applicationSelectors.getCurrentApplication(state)
});

export default connect(mapStateToProps)(ApplicationDetails);
